import React, { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  MenuItem,
  Chip,
  Tooltip,
  Alert,
} from "@mui/material";
import { RoundedCard } from "./shared/StyledComponents";
import { styled, keyframes } from "@mui/material/styles";
import RedeemIcon from "@mui/icons-material/Redeem";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TopBar from "./TopBar";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled(Box)(() => ({
  minHeight: "100vh",
  backgroundColor: "#fafafa",
  display: "flex",
  flexDirection: "column",
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(4),
  },
  overflow: "auto",
}));

const SectionCard = styled(RoundedCard)(({ theme }) => ({
  background: "#ffffff",
  border: "1px solid #e8e8e8",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${fadeIn} 0.4s ease-out`,
  padding: theme.spacing(3),
  "&:hover": {
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
  },
}));

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    transition: "all 0.2s ease",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    },
    "&.Mui-focused": {
      boxShadow: "0 4px 12px rgba(255, 183, 27, 0.15)",
    },
  },
}));

const SaveButton = styled(Button)(() => ({
  borderRadius: 14,
  textTransform: "none",
  fontWeight: 700,
  padding: "14px 32px",
  fontSize: "0.95rem",
  background: "linear-gradient(135deg, #ffb71b 0%, #ff9500 100%)",
  color: "#000",
  boxShadow: "0 4px 16px rgba(255, 183, 27, 0.3)",
  "&:hover": {
    background: "linear-gradient(135deg, #ffc94d 0%, #ffb71b 100%)",
    boxShadow: "0 6px 24px rgba(255, 183, 27, 0.4)",
    transform: "translateY(-1px)",
  },
  transition: "all 0.2s ease",
}));

const PreviewCard = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
  borderRadius: 20,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-40%",
    right: "-10%",
    width: 200,
    height: 200,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(255,183,27,0.15) 0%, transparent 70%)",
  },
}));

const IconCircle = styled(Box)(({ $bgColor = "#fff8e7" }) => ({
  width: 48,
  height: 48,
  borderRadius: 14,
  backgroundColor: $bgColor,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "1px solid #f5f5f5",
  padding: "14px 16px",
  fontSize: "0.875rem",
}));

const StyledTableHeadCell = styled(TableCell)(() => ({
  borderBottom: "2px solid #f0f0f0",
  padding: "12px 16px",
  fontWeight: 700,
  fontSize: "0.8rem",
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: 0.5,
}));

export default function Rewards() {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);

  const [punchesRequired, setPunchesRequired] = useState(10);
  const [earnMode, setEarnMode] = useState("per-visit");
  const [qualifier, setQualifier] = useState("");
  const [qualifierCustom, setQualifierCustom] = useState("");
  const [maxPerTxn, setMaxPerTxn] = useState(1);
  const [prize, setPrize] = useState("");
  const [prizeCustom, setPrizeCustom] = useState("");
  const [expiryDays, setExpiryDays] = useState(180);
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [redemptionHistory, setRedemptionHistory] = useState([]);
  const currentPunches = 0; // Preview shows empty card

  const progressPercent = useMemo(() => {
    const target = Math.max(1, punchesRequired);
    return Math.min(100, Math.round((currentPunches / target) * 100));
  }, [currentPunches, punchesRequired]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`/api/rewards/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load reward data");

        const data = await res.json();

        if (data.reward) {
          setPunchesRequired(data.reward.stamps || 10);
          setQualifier(
            data.reward.item
              ? data.reward.item
              : data.reward.itemCustom
              ? "__other"
              : ""
          );
          setQualifierCustom(data.reward.itemCustom || "");
          setPrize(
            data.reward.rewardItem
              ? data.reward.rewardItem
              : data.reward.rewardItemCustom
              ? "__other"
              : ""
          );
          setPrizeCustom(data.reward.rewardItemCustom || "");
        }

        if (data.items) {
          setItems(data.items);
        }

        if (data.redemptionHistory) {
          setRedemptionHistory(data.redemptionHistory);
        }
      } catch (err) {
        console.error("Error fetching reward setup:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  // When the vendor has no menu items, the only choice is a free-text value, so
  // we skip the dropdown entirely and edit the "custom" fields directly.
  const hasItems = items.length > 0;

  const validateForm = () => {
    const newErrors = {};
    if (!punchesRequired || punchesRequired < 1) {
      newErrors.punchesRequired = "Claims required must be at least 1";
    }
    // Qualifying item
    if (hasItems) {
      if (!qualifier) newErrors.qualifier = "Please select a qualifying item";
      if (qualifier === "__other" && !qualifierCustom.trim()) newErrors.qualifierCustom = "Please specify the qualifying item";
    } else if (!qualifierCustom.trim()) {
      newErrors.qualifierCustom = "Please specify the qualifying item";
    }
    // Reward prize
    if (hasItems) {
      if (!prize) newErrors.prize = "Please select a reward prize";
      if (prize === "__other" && !prizeCustom.trim()) newErrors.prizeCustom = "Please specify the reward prize";
    } else if (!prizeCustom.trim()) {
      newErrors.prizeCustom = "Please specify the reward prize";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // No items → always custom (item id null, custom text saved). Same DB shape
    // as picking "Other" in the dropdown — this is purely a frontend mapping.
    const qualifierCustomMode = !hasItems || qualifier === "__other";
    const prizeCustomMode = !hasItems || prize === "__other";

    const payload = {
      stamps: clampInt(punchesRequired, 1, 100),
      item: qualifierCustomMode ? null : (qualifier || null),
      itemCustom: qualifierCustomMode ? (qualifierCustom.trim() || null) : null,
      rewardItem: prizeCustomMode ? null : (prize || null),
      rewardItemCustom: prizeCustomMode ? (prizeCustom.trim() || null) : null,
    };

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/rewards/${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save reward");
      const data = await res.json();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving:", err);
    }
  };


  const iconSlots = Math.min(12, Math.max(4, punchesRequired));
  const filled = Math.min(
    iconSlots,
    Math.floor((currentPunches / Math.max(1, punchesRequired)) * iconSlots)
  );

  if (loading) return <div>Loading...</div>;

  return (
    <PageContainer>
      <TopBar />
      <ContentContainer maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" }, fontWeight: 800, color: "#111", mb: 0.5 }}
          >
            Rewards
          </Typography>
          <Typography variant="body1" sx={{ color: "#666" }}>
            Configure your rewards and track customer reward activity
          </Typography>
        </Box>

        {saveSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: 3 }}
            onClose={() => setSaveSuccess(false)}
          >
            Rewards settings saved successfully!
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: 3,
            mb: 4,
          }}
        >
          {/* Rewards Setup */}
          <SectionCard component="form" onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 3,
              }}
            >
              <IconCircle $bgColor="#fff3e0">
                <SettingsIcon sx={{ color: "#ff9800", fontSize: 24 }} />
              </IconCircle>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#222" }}
                >
                  Rewards Setup
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  Configure how customers earn rewards
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { md: "1fr 1fr" },
                gap: 2.5,
              }}
            >
              <StyledTextField
                label="Claims Required"
                type="number"
                value={punchesRequired}
                onChange={(e) =>
                  setPunchesRequired(clampInt(e.target.value, 1, 100))
                }
                error={!!errors.punchesRequired}
                helperText={errors.punchesRequired}
                fullWidth
              />

              {hasItems ? (
                <>
                  <StyledTextField
                    select
                    label="Qualifying Item"
                    value={qualifier}
                    onChange={(e) => setQualifier(e.target.value)}
                    fullWidth
                    error={!!errors.qualifier}
                    helperText={errors.qualifier}
                  >
                    {items.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.name}
                      </MenuItem>
                    ))}
                    <MenuItem value="__other">Other</MenuItem>
                  </StyledTextField>

                  {qualifier === "__other" && (
                    <StyledTextField
                      label="Other Qualifier"
                      value={qualifierCustom}
                      onChange={(e) => setQualifierCustom(e.target.value)}
                      fullWidth
                      error={!!errors.qualifierCustom}
                      helperText={errors.qualifierCustom}
                    />
                  )}
                </>
              ) : (
                <StyledTextField
                  label="Qualifying Item"
                  value={qualifierCustom}
                  onChange={(e) => setQualifierCustom(e.target.value)}
                  fullWidth
                  error={!!errors.qualifierCustom}
                  helperText={errors.qualifierCustom || "The item a customer buys to earn a stamp"}
                />
              )}

              {hasItems ? (
                <>
                  <StyledTextField
                    select
                    label="Reward Prize"
                    value={prize}
                    onChange={(e) => setPrize(e.target.value)}
                    fullWidth
                    error={!!errors.prize}
                    helperText={errors.prize || "The item the customer wins for free"}
                  >
                    {items.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.name}
                      </MenuItem>
                    ))}
                    <MenuItem value="__other">Other</MenuItem>
                  </StyledTextField>

                  {prize === "__other" && (
                    <StyledTextField
                      label="Other Prize"
                      value={prizeCustom}
                      onChange={(e) => setPrizeCustom(e.target.value)}
                      fullWidth
                      error={!!errors.prizeCustom}
                      helperText={errors.prizeCustom}
                    />
                  )}
                </>
              ) : (
                <StyledTextField
                  label="Reward Prize"
                  value={prizeCustom}
                  onChange={(e) => setPrizeCustom(e.target.value)}
                  fullWidth
                  error={!!errors.prizeCustom}
                  helperText={errors.prizeCustom || "The item the customer wins for free"}
                />
              )}
            </Box>

            <SaveButton
              type="submit"
              fullWidth
              startIcon={<SaveIcon />}
              sx={{ mt: 3 }}
            >
              Save Rewards
            </SaveButton>
          </SectionCard>

          {/* Live Preview */}
          <SectionCard>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 3,
              }}
            >
              <IconCircle $bgColor="#e3f2fd">
                <VisibilityIcon sx={{ color: "#1976d2", fontSize: 24 }} />
              </IconCircle>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#222" }}
                >
                  Live Preview
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  See how your rewards look to customers
                </Typography>
              </Box>
            </Box>

            <PreviewCard>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      Reward Progress
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ color: "#fff", fontWeight: 700 }}
                    >
                      {currentPunches} / {punchesRequired}
                    </Typography>
                  </Box>
                  <Chip
                    label="Stamp Card"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.1)",
                      color: "rgba(255,255,255,0.8)",
                      fontSize: "0.7rem",
                      fontWeight: 500,
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                    gap: 0.5,
                  }}
                >
                  {Array.from({ length: iconSlots }).map((_, i) => (
                    <Box key={i} sx={{ flex: 1, textAlign: "center" }}>
                      <RedeemIcon
                        sx={{
                          fontSize: { xs: "1.8rem", md: "2.2rem" },
                          color:
                            i < filled
                              ? "#ffb71b"
                              : "rgba(255,255,255,0.15)",
                          transition: "color 0.3s",
                        }}
                      />
                    </Box>
                  ))}
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "rgba(255,255,255,0.1)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 3,
                      background:
                        "linear-gradient(90deg, #ffb71b, #ff9500)",
                    },
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.5)" }}
                  >
                    {earnMode === "per-visit"
                      ? "1 stamp per visit"
                      : `1 stamp per item (max ${maxPerTxn}/txn)`}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.5)" }}
                  >
                    {progressPercent}% complete
                  </Typography>
                </Box>

                {((hasItems ? qualifier : qualifierCustom) || (hasItems ? prize : prizeCustom)) && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: "1px solid rgba(255,255,255,0.1)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {(hasItems ? qualifier : qualifierCustom) && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.4)",
                          fontWeight: 500,
                        }}
                      >
                        Qualifying item:{" "}
                        <span style={{ color: "rgba(255,255,255,0.7)" }}>
                          {!hasItems || qualifier === "__other"
                            ? qualifierCustom
                            : items.find((i) => i._id === qualifier)?.name ||
                              qualifier}
                        </span>
                      </Typography>
                    )}
                    {(hasItems ? prize : prizeCustom) && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.4)",
                          fontWeight: 500,
                        }}
                      >
                        Reward prize:{" "}
                        <span style={{ color: "#ffb71b" }}>
                          {!hasItems || prize === "__other"
                            ? prizeCustom
                            : items.find((i) => i._id === prize)?.name ||
                              prize}
                        </span>
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </PreviewCard>
          </SectionCard>
        </Box>

        {/* Activity Table */}
        <SectionCard>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
          >
            <IconCircle $bgColor="#f3e5f5">
              <HistoryIcon sx={{ color: "#9c27b0", fontSize: 24 }} />
            </IconCircle>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#222" }}
              >
                Reward Activity
              </Typography>
              <Typography variant="body2" sx={{ color: "#888" }}>
                Recent customer redemptions
              </Typography>
            </Box>
            <Chip
              label={`${redemptionHistory.length} total`}
              size="small"
              sx={{
                backgroundColor: "#f5f5f5",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          </Box>

          <Box
            sx={{
              overflowX: "auto",
              borderRadius: 3,
              border: "1px solid #f0f0f0",
              // Three columns won't fit a phone; scroll the table rather than
              // letting it push the whole page sideways.
              overflowX: "auto",
            }}
          >
            <Table sx={{ minWidth: 420 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#fafafa" }}>
                  <StyledTableHeadCell>Customer</StyledTableHeadCell>
                  <StyledTableHeadCell>Date</StyledTableHeadCell>
                  <StyledTableHeadCell>Reward</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {redemptionHistory.length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={3} sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body2" sx={{ color: "#999" }}>
                        No redemptions yet. Activity will appear here as customers earn rewards.
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  redemptionHistory.map((entry) => (
                    <TableRow
                      key={entry.id}
                      sx={{
                        transition: "background 0.2s",
                        "&:hover": { backgroundColor: "#fafafa" },
                      }}
                    >
                      <StyledTableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              backgroundColor: "#fff8e7",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              color: "#ffb71b",
                            }}
                          >
                            {entry.customer.charAt(0)}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: "#333" }}
                          >
                            {entry.customer}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {entry.date}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          icon={
                            <CardGiftcardIcon
                              sx={{ fontSize: 14, color: "#4caf50 !important" }}
                            />
                          }
                          label={entry.reward}
                          size="small"
                          sx={{
                            backgroundColor: "#e8f5e9",
                            color: "#2e7d32",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                      </StyledTableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </SectionCard>
      </ContentContainer>
    </PageContainer>
  );
}

// ------- helpers -------
function clampInt(v, min, max) {
  const n = Number(v);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.trunc(n)));
}
